import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
    Bike, Plus, Trash2, Pencil, Phone, MapPin, Package,
    RefreshCcw, Users, XCircle, CheckCircle2, Clock, Truck, Navigation
} from 'lucide-react';

const STATUS_LABEL = {
    pending: 'Pendiente',
    assigned: 'Asignado',
    picked_up: 'Recogido',
    in_transit: 'En tránsito',
    delivered: 'Entregado',
    cancelled: 'Cancelado',
    failed: 'Fallido',
};

const STATUS_CLASSES = {
    pending: 'bg-amber-100 text-amber-800',
    assigned: 'bg-blue-100 text-blue-800',
    picked_up: 'bg-indigo-100 text-indigo-800',
    in_transit: 'bg-violet-100 text-violet-800',
    delivered: 'bg-emerald-100 text-emerald-800',
    cancelled: 'border border-destructive text-destructive',
    failed: 'border border-red-400 text-red-700',
};

const VEHICLE_OPTIONS = [
    { value: 'motorcycle', label: 'Motocicleta' },
    { value: 'bicycle', label: 'Bicicleta' },
    { value: 'car', label: 'Auto' },
    { value: 'van', label: 'Camioneta' },
    { value: 'on_foot', label: 'A pie' },
];

const emptyOrder = {
    pickup_address: '',
    pickup_contact: '',
    pickup_phone: '',
    pickup_notes: '',
    dropoff_address: '',
    recipient_name: '',
    recipient_phone: '',
    delivery_notes: '',
    package_description: 'Paquete',
    weight_kg: 1,
    declared_value: 0,
    order_id: '',
};

const emptyDriver = {
    name: '',
    phone: '',
    email: '',
    vehicle: 'motorcycle',
    license_plate: '',
    notes: '',
};

export default function AliadoOkDeliveryPage() {
    const [tab, setTab] = useState('deliveries');
    const [deliveries, setDeliveries] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create delivery dialog
    const [showCreate, setShowCreate] = useState(false);
    const [orderForm, setOrderForm] = useState(emptyOrder);
    const [creating, setCreating] = useState(false);

    // Driver management
    const [showDriverForm, setShowDriverForm] = useState(false);
    const [editingDriver, setEditingDriver] = useState(null);
    const [driverForm, setDriverForm] = useState(emptyDriver);
    const [savingDriver, setSavingDriver] = useState(false);
    const [deleteDriverId, setDeleteDriverId] = useState(null);

    // Assign driver dialog
    const [assignDelivery, setAssignDelivery] = useState(null);
    const [assignDriverId, setAssignDriverId] = useState('');
    const [assigning, setAssigning] = useState(false);

    // Cancel delivery
    const [cancelId, setCancelId] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: d }, { data: dr }] = await Promise.all([
                api.get('/me/oknegocio/delivery/orders'),
                api.get('/me/oknegocio/delivery/drivers'),
            ]);
            setDeliveries(d.items || []);
            setDrivers(dr || []);
        } catch { toast.error('Error al cargar OkDelivery'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // ── Create delivery ──────────────────────────────────────────────────────
    const handleCreateDelivery = async () => {
        if (!orderForm.pickup_address || !orderForm.dropoff_address || !orderForm.recipient_name || !orderForm.recipient_phone) {
            toast.error('Completa los campos obligatorios');
            return;
        }
        setCreating(true);
        try {
            await api.post('/me/oknegocio/delivery/orders', orderForm);
            toast.success('Envío creado correctamente');
            setShowCreate(false);
            setOrderForm(emptyOrder);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al crear envío'); }
        finally { setCreating(false); }
    };

    // ── Cancel delivery ──────────────────────────────────────────────────────
    const handleCancel = async () => {
        if (!cancelId) return;
        setCancelling(true);
        try {
            await api.post(`/me/oknegocio/delivery/orders/${cancelId}/cancel`, {});
            toast.success('Envío cancelado');
            setCancelId(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setCancelling(false); }
    };

    // ── Assign driver ────────────────────────────────────────────────────────
    const handleAssignDriver = async () => {
        if (!assignDriverId || assignDriverId === '__none__') {
            toast.error('Selecciona un repartidor');
            return;
        }
        setAssigning(true);
        try {
            await api.post(`/me/oknegocio/delivery/orders/${assignDelivery.id}/assign-driver`, { driver_id: assignDriverId });
            toast.success('Repartidor asignado');
            setAssignDelivery(null);
            setAssignDriverId('');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setAssigning(false); }
    };

    // ── Driver CRUD ──────────────────────────────────────────────────────────
    const openAddDriver = () => { setEditingDriver(null); setDriverForm(emptyDriver); setShowDriverForm(true); };
    const openEditDriver = (dr) => { setEditingDriver(dr); setDriverForm({ name: dr.name, phone: dr.phone, email: dr.email || '', vehicle: dr.vehicle || 'motorcycle', license_plate: dr.license_plate || '', notes: dr.notes || '' }); setShowDriverForm(true); };

    const handleSaveDriver = async () => {
        if (!driverForm.name || !driverForm.phone) { toast.error('Nombre y teléfono son requeridos'); return; }
        setSavingDriver(true);
        try {
            if (editingDriver) {
                await api.put(`/me/oknegocio/delivery/drivers/${editingDriver.id}`, driverForm);
                toast.success('Repartidor actualizado');
            } else {
                await api.post('/me/oknegocio/delivery/drivers', driverForm);
                toast.success('Repartidor agregado');
            }
            setShowDriverForm(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setSavingDriver(false); }
    };

    const handleDeleteDriver = async (id) => {
        setDeleteDriverId(null);
        try {
            await api.delete(`/me/oknegocio/delivery/drivers/${id}`);
            toast.success('Repartidor eliminado');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    // ── Active summary ───────────────────────────────────────────────────────
    const activeCount = deliveries.filter(d => ['pending', 'assigned', 'picked_up', 'in_transit'].includes(d.status)).length;
    const deliveredCount = deliveries.filter(d => d.status === 'delivered').length;

    return (
        <div className="space-y-6" data-testid="aliado-okdelivery-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Bike className="h-6 w-6 text-orange-600" />OkDelivery
                    </h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Gestiona envíos y repartidores con Picker Express</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                        <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refrescar
                    </Button>
                    <Button size="sm" onClick={() => setShowCreate(true)} className="bg-orange-600 hover:bg-orange-700">
                        <Plus className="h-4 w-4 mr-1" />Nuevo envío
                    </Button>
                </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: 'Total', value: deliveries.length, icon: Package },
                    { label: 'Activos', value: activeCount, icon: Truck, cls: 'text-violet-700' },
                    { label: 'Entregados', value: deliveredCount, icon: CheckCircle2, cls: 'text-emerald-700' },
                    { label: 'Repartidores', value: drivers.length, icon: Users, cls: 'text-blue-700' },
                ].map(({ label, value, icon: Icon, cls }) => (
                    <Card key={label}><CardContent className="p-4">
                        <div className="flex items-center gap-1.5 text-xs uppercase text-muted-foreground mb-1">
                            <Icon className="h-3.5 w-3.5" />{label}
                        </div>
                        <div className={`text-3xl font-bold ${cls || ''}`}>{value}</div>
                    </CardContent></Card>
                ))}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="deliveries">
                        <Package className="h-3.5 w-3.5 mr-1" />Envíos activos
                    </TabsTrigger>
                    <TabsTrigger value="history">
                        <Clock className="h-3.5 w-3.5 mr-1" />Historial
                    </TabsTrigger>
                    <TabsTrigger value="drivers">
                        <Users className="h-3.5 w-3.5 mr-1" />Mis repartidores
                    </TabsTrigger>
                </TabsList>

                {/* ── Active deliveries ── */}
                <TabsContent value="deliveries" className="pt-4">
                    <DeliveryList
                        items={deliveries.filter(d => !['delivered', 'cancelled', 'failed'].includes(d.status))}
                        drivers={drivers}
                        onCancel={setCancelId}
                        onAssign={(d) => { setAssignDelivery(d); setAssignDriverId(''); }}
                        emptyMsg="No tienes envíos activos en este momento."
                    />
                </TabsContent>

                {/* ── History ── */}
                <TabsContent value="history" className="pt-4">
                    <DeliveryList
                        items={deliveries.filter(d => ['delivered', 'cancelled', 'failed'].includes(d.status))}
                        drivers={drivers}
                        onCancel={null}
                        onAssign={null}
                        emptyMsg="Aún no hay envíos completados o cancelados."
                    />
                </TabsContent>

                {/* ── Own drivers ── */}
                <TabsContent value="drivers" className="pt-4">
                    <div className="flex justify-end mb-3">
                        <Button size="sm" onClick={openAddDriver} className="bg-orange-600 hover:bg-orange-700">
                            <Plus className="h-4 w-4 mr-1" />Agregar repartidor
                        </Button>
                    </div>
                    {drivers.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Bike className="h-10 w-10 mx-auto mb-3 opacity-30" />
                            <p className="text-sm">Aún no tienes repartidores propios registrados.</p>
                        </div>
                    ) : (
                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
                                    <tr>
                                        <th className="p-2 text-left">Nombre</th>
                                        <th className="text-left">Teléfono</th>
                                        <th className="text-left">Vehículo</th>
                                        <th className="text-left">Placa</th>
                                        <th>Estado</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drivers.map(dr => (
                                        <tr key={dr.id} className="border-b last:border-0 hover:bg-slate-50">
                                            <td className="p-2 font-medium">{dr.name}</td>
                                            <td>
                                                <a href={`tel:${dr.phone}`} className="flex items-center gap-1 text-primary hover:underline">
                                                    <Phone className="h-3 w-3" />{dr.phone}
                                                </a>
                                            </td>
                                            <td className="capitalize">{VEHICLE_OPTIONS.find(v => v.value === dr.vehicle)?.label || dr.vehicle || '—'}</td>
                                            <td className="font-mono text-xs">{dr.license_plate || '—'}</td>
                                            <td className="text-center">
                                                {dr.active
                                                    ? <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge>
                                                    : <Badge variant="outline" className="text-muted-foreground">Inactivo</Badge>
                                                }
                                            </td>
                                            <td className="text-right pr-2">
                                                <div className="flex gap-1 justify-end">
                                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDriver(dr)}>
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteDriverId(dr.id)}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* ── Create delivery dialog ── */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bike className="h-5 w-5 text-orange-600" />Nuevo envío
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Origen (pickup)</p>
                        <div className="space-y-3 pl-1 border-l-2 border-orange-200">
                            <div>
                                <Label>Dirección de recogida <span className="text-destructive">*</span></Label>
                                <Input value={orderForm.pickup_address} onChange={e => setOrderForm(f => ({ ...f, pickup_address: e.target.value }))} placeholder="Calle, número, colonia, ciudad" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label>Contacto origen</Label>
                                    <Input value={orderForm.pickup_contact} onChange={e => setOrderForm(f => ({ ...f, pickup_contact: e.target.value }))} placeholder="Nombre del contacto" />
                                </div>
                                <div>
                                    <Label>Teléfono origen</Label>
                                    <Input value={orderForm.pickup_phone} onChange={e => setOrderForm(f => ({ ...f, pickup_phone: e.target.value }))} placeholder="10 dígitos" />
                                </div>
                            </div>
                            <div>
                                <Label>Notas de recogida</Label>
                                <Input value={orderForm.pickup_notes} onChange={e => setOrderForm(f => ({ ...f, pickup_notes: e.target.value }))} placeholder="Referencia, piso, etc." />
                            </div>
                        </div>

                        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Destino (dropoff)</p>
                        <div className="space-y-3 pl-1 border-l-2 border-blue-200">
                            <div>
                                <Label>Dirección de entrega <span className="text-destructive">*</span></Label>
                                <Input value={orderForm.dropoff_address} onChange={e => setOrderForm(f => ({ ...f, dropoff_address: e.target.value }))} placeholder="Calle, número, colonia, ciudad" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label>Nombre del destinatario <span className="text-destructive">*</span></Label>
                                    <Input value={orderForm.recipient_name} onChange={e => setOrderForm(f => ({ ...f, recipient_name: e.target.value }))} placeholder="Nombre completo" />
                                </div>
                                <div>
                                    <Label>Teléfono destinatario <span className="text-destructive">*</span></Label>
                                    <Input value={orderForm.recipient_phone} onChange={e => setOrderForm(f => ({ ...f, recipient_phone: e.target.value }))} placeholder="10 dígitos" />
                                </div>
                            </div>
                            <div>
                                <Label>Notas de entrega</Label>
                                <Input value={orderForm.delivery_notes} onChange={e => setOrderForm(f => ({ ...f, delivery_notes: e.target.value }))} placeholder="Instrucciones especiales" />
                            </div>
                        </div>

                        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Paquete</p>
                        <div className="space-y-3 pl-1 border-l-2 border-slate-200">
                            <div>
                                <Label>Descripción del paquete</Label>
                                <Input value={orderForm.package_description} onChange={e => setOrderForm(f => ({ ...f, package_description: e.target.value }))} placeholder="Ej: Comida, ropa, electrónico" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label>Peso (kg)</Label>
                                    <Input type="number" min="0.1" step="0.1" value={orderForm.weight_kg} onChange={e => setOrderForm(f => ({ ...f, weight_kg: parseFloat(e.target.value) || 1 }))} />
                                </div>
                                <div>
                                    <Label>Valor declarado (MXN)</Label>
                                    <Input type="number" min="0" value={orderForm.declared_value} onChange={e => setOrderForm(f => ({ ...f, declared_value: parseFloat(e.target.value) || 0 }))} />
                                </div>
                            </div>
                            <div>
                                <Label>ID de orden (opcional)</Label>
                                <Input value={orderForm.order_id} onChange={e => setOrderForm(f => ({ ...f, order_id: e.target.value }))} placeholder="Referencia interna" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={handleCreateDelivery} disabled={creating} className="bg-orange-600 hover:bg-orange-700">
                            {creating ? 'Creando…' : 'Crear envío'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Cancel confirmation ── */}
            <Dialog open={!!cancelId} onOpenChange={() => setCancelId(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>¿Cancelar este envío?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">Esta acción notificará a Picker Express para cancelar la solicitud. No se puede deshacer.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCancelId(null)}>No, mantener</Button>
                        <Button variant="destructive" onClick={handleCancel} disabled={cancelling}>
                            {cancelling ? 'Cancelando…' : 'Sí, cancelar envío'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Assign driver dialog ── */}
            <Dialog open={!!assignDelivery} onOpenChange={() => setAssignDelivery(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Asignar repartidor propio</DialogTitle>
                    </DialogHeader>
                    {drivers.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-2">No tienes repartidores propios. Agrégalos en la pestaña "Mis repartidores" primero.</p>
                    ) : (
                        <div className="py-2">
                            <Label>Repartidor</Label>
                            <Select value={assignDriverId} onValueChange={setAssignDriverId}>
                                <SelectTrigger><SelectValue placeholder="Selecciona un repartidor" /></SelectTrigger>
                                <SelectContent>
                                    {drivers.filter(d => d.active).map(d => (
                                        <SelectItem key={d.id} value={d.id}>{d.name} · {d.phone}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignDelivery(null)}>Cancelar</Button>
                        {drivers.length > 0 && (
                            <Button onClick={handleAssignDriver} disabled={assigning} className="bg-orange-600 hover:bg-orange-700">
                                {assigning ? 'Asignando…' : 'Asignar'}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Driver form dialog ── */}
            <Dialog open={showDriverForm} onOpenChange={setShowDriverForm}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingDriver ? 'Editar repartidor' : 'Agregar repartidor'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label>Nombre <span className="text-destructive">*</span></Label>
                                <Input value={driverForm.name} onChange={e => setDriverForm(f => ({ ...f, name: e.target.value }))} placeholder="Nombre completo" />
                            </div>
                            <div>
                                <Label>Teléfono <span className="text-destructive">*</span></Label>
                                <Input value={driverForm.phone} onChange={e => setDriverForm(f => ({ ...f, phone: e.target.value }))} placeholder="10 dígitos" />
                            </div>
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input type="email" value={driverForm.email} onChange={e => setDriverForm(f => ({ ...f, email: e.target.value }))} placeholder="correo@ejemplo.com" />
                        </div>
                        <div>
                            <Label>Vehículo</Label>
                            <Select value={driverForm.vehicle} onValueChange={v => setDriverForm(f => ({ ...f, vehicle: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {VEHICLE_OPTIONS.map(v => (
                                        <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Placa</Label>
                            <Input value={driverForm.license_plate} onChange={e => setDriverForm(f => ({ ...f, license_plate: e.target.value }))} placeholder="ABC-1234" />
                        </div>
                        <div>
                            <Label>Notas</Label>
                            <Input value={driverForm.notes} onChange={e => setDriverForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observaciones" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDriverForm(false)}>Cancelar</Button>
                        <Button onClick={handleSaveDriver} disabled={savingDriver} className="bg-orange-600 hover:bg-orange-700">
                            {savingDriver ? 'Guardando…' : (editingDriver ? 'Guardar cambios' : 'Agregar')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Delete driver confirm ── */}
            <Dialog open={!!deleteDriverId} onOpenChange={() => setDeleteDriverId(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>¿Eliminar repartidor?</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDriverId(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={() => handleDeleteDriver(deleteDriverId)}>Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ── Delivery list component ──────────────────────────────────────────────────
function DeliveryList({ items, drivers, onCancel, onAssign, emptyMsg }) {
    if (items.length === 0) {
        return (
            <div className="text-center py-12 text-muted-foreground">
                <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">{emptyMsg}</p>
            </div>
        );
    }

    const canAct = (status) => !['delivered', 'cancelled', 'failed'].includes(status);

    return (
        <div className="space-y-3">
            {items.map(d => (
                <Card key={d.id} className="overflow-hidden">
                    <CardContent className="p-0">
                        <div className="flex items-start justify-between p-4 gap-3">
                            <div className="flex-1 min-w-0 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className={STATUS_CLASSES[d.status] || ''}>
                                        {STATUS_LABEL[d.status] || d.status}
                                    </Badge>
                                    {d.driver_name && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Bike className="h-3 w-3" />{d.driver_name}
                                        </span>
                                    )}
                                    {d.picker_order_id && (
                                        <span className="text-xs font-mono text-muted-foreground">#{d.picker_order_id}</span>
                                    )}
                                </div>
                                <div className="flex items-start gap-1 text-sm">
                                    <MapPin className="h-3.5 w-3.5 text-orange-500 mt-0.5 shrink-0" />
                                    <span className="text-muted-foreground text-xs">{d.pickup_address}</span>
                                </div>
                                <div className="flex items-start gap-1 text-sm">
                                    <Navigation className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
                                    <span className="text-xs"><strong>{d.recipient_name}</strong> · {d.dropoff_address}</span>
                                </div>
                                {d.package_description && (
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <Package className="h-3 w-3" />{d.package_description} · {d.weight_kg} kg
                                        {d.delivery_fee ? <span className="ml-1 font-mono">${Number(d.delivery_fee).toFixed(2)} MXN</span> : null}
                                    </div>
                                )}
                                <div className="text-[10px] text-muted-foreground">
                                    {d.created_at ? new Date(d.created_at).toLocaleString('es-MX') : ''}
                                </div>
                            </div>
                            {canAct(d.status) && (
                                <div className="flex flex-col gap-1.5 shrink-0">
                                    {onAssign && drivers.length > 0 && (
                                        <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => onAssign(d)}>
                                            <Users className="h-3 w-3 mr-1" />Asignar
                                        </Button>
                                    )}
                                    {onCancel && (
                                        <Button variant="outline" size="sm" className="text-xs h-7 text-destructive hover:text-destructive border-destructive/40" onClick={() => onCancel(d.id)}>
                                            <XCircle className="h-3 w-3 mr-1" />Cancelar
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
